import React from 'react';

import { BED_STATE_META } from '../../../constants/occupancy';

/**
 * BedChip (C2 / FT5) — one bed tile in the ward grid (Variant A).
 *
 * State is a CODE from the board payload; the colour + label + dot are mapped
 * client-side from the single-source BED_STATE_META. Accessibility: the state is
 * always shown as a text LABEL beside a colour dot — never colour-only (WCAG),
 * legible in light + dark. The bed CATEGORY (GEN/PVT/ICU) shows as a small badge.
 * On OCCUPIED the denormalized patient name + IP number come straight from the
 * board payload — there is NO per-bed fetch (staff-authenticated view). A VACANT
 * bed rendered from a version-0 sparse row is a normal tile, never hidden.
 *
 * Death-record flag (FE-5, pairs with BE-6 — the clinically-important one, D43):
 * `bed.death_recorded` (bool, absent ⇒ false, S8) renders a text LABEL beside a
 * red dot, same accessibility rule as the state dot — never colour alone.
 * Without this a patient who has died on this bed reads as an ordinary occupied
 * bed. The flag is driven straight off the board payload / live WS overlay —
 * no per-bed fetch — so it must also disappear the moment the payload clears it
 * (discharge, re-occupancy, or the death record being removed).
 */
export default function BedChip({ bed, onClick, justUpdated = false, highlight = false }) {
  const meta = BED_STATE_META[bed.state] || BED_STATE_META.VACANT;
  const occupied = bed.state === 'OCCUPIED';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`bed-chip-${bed.bed_id}`}
      data-state={bed.state}
      data-highlight={highlight ? 'true' : undefined}
      className={`relative w-[168px] min-h-[70px] rounded-lg border px-2.5 py-2 text-left
        focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${meta.cls}
        ${justUpdated ? 'ring-2 ring-teal-400 dark:ring-teal-300' : ''}
        ${highlight ? 'ring-2 ring-amber-400 dark:ring-amber-300' : ''}`}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span className="truncate text-[12.5px] font-semibold">{bed.label}</span>
        <span className="inline-flex shrink-0 items-center gap-1 text-[10.5px] font-semibold">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: meta.dot }}
            aria-hidden="true"
          />
          {meta.label}
        </span>
      </div>

      {bed.bed_category_code && (
        <span
          data-testid={`bed-cat-${bed.bed_id}`}
          className="mt-1 inline-block rounded border border-current/30 px-1 py-px text-[9.5px] font-semibold uppercase tracking-wide opacity-80"
        >
          {bed.bed_category_code}
        </span>
      )}

      {occupied && bed.patient_name && (
        <div className="mt-1 truncate text-[12px] font-medium" data-testid={`bed-patient-${bed.bed_id}`}>
          {bed.patient_name}
        </div>
      )}
      {occupied && bed.ip_number && (
        <div className="truncate text-[10.5px] opacity-80">{bed.ip_number}</div>
      )}

      {/* [NEEDS-PO] copy — "Deceased" flag label on the bed tile. */}
      {bed.death_recorded && (
        <div
          className="mt-1 flex items-center gap-1 text-[10.5px] font-bold text-red-700 dark:text-red-300"
          data-testid={`bed-death-${bed.bed_id}`}
        >
          <span className="inline-block h-2 w-2 rounded-full bg-red-600" aria-hidden="true" />
          Deceased
        </div>
      )}

      {justUpdated && (
        <span className="absolute right-1.5 top-1.5 rounded-full bg-teal-600 px-1.5 py-px text-[9px] font-bold text-white">
          just now
        </span>
      )}
    </button>
  );
}
